import { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";

/**
 * Vigia de versão nova.
 *
 * ── POR QUE NÃO RECARREGA SOZINHO COM A PESSOA USANDO ───────────────────────
 * Recarregava. A cada 20 segundos ele comparava o `buildId` do `version.json`
 * com o do bundle carregado e, na primeira diferença, dava
 * `location.replace()` na hora — no meio de um formulário, de uma leitura, do
 * que fosse. Em 10/09/2026 isso apareceu do outro lado: cliente relatou tela
 * quebrada logo depois de um deploy e concluiu que "o site tinha caído".
 * Recarga forçada e sem aviso é indistinguível de queda para quem está do
 * outro lado da tela.
 *
 * Agora: aba ESCONDIDA recarrega sozinha (ninguém está olhando, é de graça);
 * aba em USO ganha um aviso discreto com um botão. O usuário decide quando.
 */

type VersionPayload = {
  version?: string;
  buildId?: string;
  builtAt?: string;
};

/** Lido na hora, e não no topo do módulo: assim o teste consegue trocá-lo. */
const buildIdCarregado = (): string | undefined => import.meta.env.VITE_APP_BUILD_ID;
const VERSION_CHECK_INTERVAL_MS = 20_000;
const RELOAD_GUARD_KEY = "north:last-reload-target-build";

const getVersionUrl = () => {
  const basePath = import.meta.env.BASE_URL === "./" ? "/" : import.meta.env.BASE_URL;
  const normalizedBase = basePath.endsWith("/") ? basePath : `${basePath}/`;
  return new URL(`${normalizedBase}version.json`, window.location.origin);
};

const buildReloadUrl = (targetBuildId: string) => {
  const url = new URL(window.location.href);
  url.searchParams.set("__build", targetBuildId);
  return url.toString();
};

export const AppVersionMonitor = () => {
  const latestBuildIdRef = useRef<string | null>(null);
  const [novaVersao, setNovaVersao] = useState<string | null>(null);

  useEffect(() => {
    if (import.meta.env.DEV || typeof window === "undefined") {
      return;
    }

    let isDisposed = false;

    const reloadToLatestVersion = (targetBuildId: string) => {
      const lastAttemptedBuild = sessionStorage.getItem(RELOAD_GUARD_KEY);

      if (lastAttemptedBuild === targetBuildId) {
        return;
      }

      sessionStorage.setItem(RELOAD_GUARD_KEY, targetBuildId);
      window.location.replace(buildReloadUrl(targetBuildId));
    };

    const scheduleReload = (targetBuildId: string) => {
      latestBuildIdRef.current = targetBuildId;
      // Aba escondida: recarrega agora, ninguém perde nada. Aba em uso: só
      // avisa — quem escolhe a hora é quem está trabalhando nela.
      if (document.visibilityState === "hidden") {
        reloadToLatestVersion(targetBuildId);
        return;
      }
      setNovaVersao(targetBuildId);
    };

    const checkForNewVersion = async () => {
      try {
        const versionUrl = getVersionUrl();
        versionUrl.searchParams.set("t", Date.now().toString());

        const response = await fetch(versionUrl.toString(), {
          cache: "no-store",
          headers: {
            "cache-control": "no-cache, no-store, must-revalidate",
            pragma: "no-cache",
          },
        });

        if (!response.ok) {
          return;
        }

        const payload = (await response.json()) as VersionPayload;
        const latestBuildId = payload.buildId;

        if (!latestBuildId || latestBuildId === buildIdCarregado() || isDisposed) {
          return;
        }

        scheduleReload(latestBuildId);
      } catch (error) {
        console.error("Erro ao verificar nova versao da aplicacao:", error);
      }
    };

    const handleVisibilityChange = () => {
      const pendingBuildId = latestBuildIdRef.current;

      if (!pendingBuildId) {
        return;
      }

      if (document.visibilityState === "hidden") {
        reloadToLatestVersion(pendingBuildId);
      }
    };

    const handleFocus = () => {
      void checkForNewVersion();
    };

    void checkForNewVersion();

    const intervalId = window.setInterval(() => {
      void checkForNewVersion();
    }, VERSION_CHECK_INTERVAL_MS);

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      isDisposed = true;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const atualizarAgora = useCallback(() => {
    // O botão é decisão explícita: limpa a guarda para a recarga valer mesmo
    // que esta versão já tenha sido tentada antes nesta aba.
    try {
      sessionStorage.removeItem(RELOAD_GUARD_KEY);
    } catch {
      // sessionStorage bloqueado: recarrega do mesmo jeito.
    }
    window.location.replace(buildReloadUrl(novaVersao ?? ""));
  }, [novaVersao]);

  if (!novaVersao) return null;

  return (
    <div
      role="status"
      className="fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-full border border-[rgba(53,70,96,0.15)] bg-card px-4 py-2 shadow-lg"
    >
      <span className="text-sm">Nova versão da plataforma disponível.</span>
      <Button type="button" size="sm" onClick={atualizarAgora} className="h-7 gap-1.5">
        <RefreshCw className="h-3.5 w-3.5" />
        Atualizar
      </Button>
    </div>
  );
};